import { useCallback, useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { downloadFromUrl } from 'utils/download-blob';
import { useEval3Page } from './context';
import { fetchAriesLatestForCalculationBaseAtom } from './atoms';
import { useAriesMdbProgress } from './use-aries-mdb-progress';
import { useAriesMdbExport } from './use-aries-mdb-export';
import type { AriesMdbExportModalProps } from 'components/aries-mdb-export';
import type {
  AriesMdbStepEvent,
  AriesMdbExportConfig,
  AriesRestoredRun,
} from 'components/aries-mdb-export/types';

export interface AriesMdbExportFlow {
  /**
   * Opens the export modal — a fresh Config for a new run, or straight into the
   * live progress view when a run is still in flight (which also blocks a second
   * kickoff).
   */
  open: () => void;
  /** Ready-to-spread props for `<AriesMdbExportModal>`. */
  modalProps: AriesMdbExportModalProps;
}

/**
 * Encapsulates the whole Aries MDB export flow — the config modal's data, its
 * live per-step progress, and per-calc restore — behind one hook, so the header
 * "Actions" menu (`ActionsMenu`) stays a thin container: it renders the trigger
 * (`open`) as a menu item and spreads `modalProps` onto the modal, with no Aries
 * state of its own. Ported from the legacy `eval-3-workspace` panel (same config
 * modal + progress hook + kickoff/defaults/restore atoms).
 *
 * All Aries run state lives HERE (not in the heavy `Eval3NewContent` root) so:
 *   - live per-step Pusher churn re-renders only the small `ActionsMenu` subtree
 *     that calls this hook, never the page root (map / table / summary);
 *   - the run survives the modal opening/closing (this hook stays mounted with
 *     the header for the page's whole life), so "Run in background" keeps
 *     tracking to completion.
 *
 * The selected calc is the route `calculationId` (each eval is its own route);
 * the restore effect re-derives the newly-selected calc's run on every route
 * change, and no-ops (clearing state) when no calc is selected.
 *
 * @return {AriesMdbExportFlow} The menu trigger + ready-to-spread modal props.
 */
export function useAriesMdbExportFlow(): AriesMdbExportFlow {
  const { calculationId } = useEval3Page();

  // ---------------------------------------------------------------------------
  // Two sources feed the progress hook, never at once:
  //   • Fresh kickoff — `open` shows the config modal; on Export the kickoff
  //     201's notifications become `events` + `channel` (all rows start Pending,
  //     live subscription bound).
  //   • Restore — the selected calc's latest run is re-derived from the server
  //     (`latest_for_calculation`) on route change / reload and handed in as
  //     `restore`. The server is the source of truth, so switching calcs shows
  //     the newly-selected calc's own run (or nothing) — no cross-calc bleed.
  // ---------------------------------------------------------------------------
  const [ariesMdbChannel, setAriesMdbChannel] = useState<string | null>(null);
  const [ariesMdbEvents, setAriesMdbEvents] = useState<
    AriesMdbStepEvent[] | null
  >(null);
  // The selected calc's restored run (null for a fresh run or a calc with none).
  const [ariesMdbRestore, setAriesMdbRestore] =
    useState<AriesRestoredRun | null>(null);
  const [isAriesMdbModalOpen, setIsAriesMdbModalOpen] = useState(false);
  // Flips the modal from the Config form to the live run views once a kickoff
  // succeeds. Reset to false on every (re)open so a fresh open always shows
  // Config — even if a prior run's `overallStatus` is still terminal.
  const [ariesMdbRunStarted, setAriesMdbRunStarted] = useState(false);
  const fetchAriesLatest = useSetAtom(fetchAriesLatestForCalculationBaseAtom);
  const ariesMdbProgress = useAriesMdbProgress({
    channel: ariesMdbChannel,
    events: ariesMdbEvents,
    restore: ariesMdbRestore,
  });
  // The restored run's config prefills the modal on a re-run; null falls back to
  // the server-derived `/new` defaults.
  const ariesMdbExport = useAriesMdbExport(
    calculationId,
    ariesMdbRestore?.config ?? null
  );
  const {
    loadDefaults: loadAriesMdbDefaults,
    submit: submitAriesMdbExport,
    clearError: clearAriesMdbError,
  } = ariesMdbExport;

  const handleOpenAriesMdbModal = useCallback(() => {
    if (calculationId === null) return;
    // If a run is still in flight, reopen straight into its live progress view
    // instead of a fresh Config — "Run in background" keeps `runStarted` and the
    // channel/events, so we only need to re-show the modal. This also stops a
    // second run being kicked off while one is already going. Only fall back to a
    // fresh Config (reset + reload defaults) when nothing is running or the last
    // run already finished/failed.
    const runInFlight =
      ariesMdbChannel !== null &&
      ariesMdbProgress.overallStatus !== 'processed' &&
      ariesMdbProgress.overallStatus !== 'failed';
    if (runInFlight) {
      setIsAriesMdbModalOpen(true);
      return;
    }
    setAriesMdbRunStarted(false);
    setIsAriesMdbModalOpen(true);
    // `loadDefaults` also clears any prior error, so the modal opens clean.
    loadAriesMdbDefaults();
  }, [
    calculationId,
    ariesMdbChannel,
    ariesMdbProgress.overallStatus,
    loadAriesMdbDefaults,
  ]);

  const handleCloseAriesMdbModal = useCallback(() => {
    setIsAriesMdbModalOpen(false);
    clearAriesMdbError();
  }, [clearAriesMdbError]);

  // Progress-view "Run in background": hide the modal but leave the run going —
  // this hook keeps tracking it, and the auto-download effect fires the file the
  // moment it completes.
  const handleRunInBackgroundAriesMdb = useCallback(() => {
    setIsAriesMdbModalOpen(false);
  }, []);

  const handleSubmitAriesMdb = useCallback(
    async (config: AriesMdbExportConfig) => {
      const response = await submitAriesMdbExport(config);
      if (!response) return; // Error stays inline in the modal; keep it open.
      // Hand the kickoff notifications to the live-progress hook (a fresh run
      // carries a new channel + events, so `useAriesMdbProgress` re-initializes
      // the checklist and re-subscribes on its own). Clear any restored run so
      // the fresh live events aren't shadowed by the seed path. Keep the modal
      // OPEN and flip it to the live progress view.
      setAriesMdbRestore(null);
      setAriesMdbEvents(response.resources.notifications.events);
      setAriesMdbChannel(response.resources.notifications.channel);
      setAriesMdbRunStarted(true);
    },
    [submitAriesMdbExport]
  );

  // Per-calc restore: re-derive the selected calc's latest Aries run from the
  // server (source of truth) whenever the selected calc changes — covering both
  // route change and page reload. An in-progress run resumes (seed +
  // re-subscribe), a terminal run seeds its completion/failed state (+ re-offers
  // the download), and a calc with no run clears any prior calc's state so
  // nothing bleeds across calcs.
  useEffect(() => {
    // Clear the prior calc's run state SYNCHRONOUSLY, before the async fetch —
    // otherwise, during the network round-trip, the previous calc's modal,
    // download, and (worse) still-subscribed live Pusher channel keep rendering
    // and applying under the newly-selected calc. This synchronous teardown is
    // the anti-bleed guarantee; the `cancelled` guard below only stops a stale
    // out-of-order resolve from seeding once the fetch returns.
    setIsAriesMdbModalOpen(false);
    setAriesMdbRunStarted(false);
    setAriesMdbRestore(null);
    setAriesMdbEvents(null);
    setAriesMdbChannel(null);

    if (calculationId === null) return;

    let cancelled = false;
    const calcId = calculationId;
    (async () => {
      const restored = await fetchAriesLatest(calcId);
      // A newer calc selection may have superseded this fetch — only the latest
      // selected calc may seed state.
      if (cancelled) return;

      setAriesMdbRestore(restored);
      // Only keep a live subscription for a run that is still in progress; a
      // terminal run is seeded directly from the payload, and no run stays clear.
      setAriesMdbChannel(restored?.should_subscribe ? restored.channel : null);
      // Mark an in-progress restored run as started so reopening the entry point
      // lands in the live progress view (and the duplicate-kickoff guard stays
      // armed). A terminal run keeps runStarted=false so reopen falls to a fresh
      // Config for a re-run — which the restored config then prefills.
      setAriesMdbRunStarted(!!restored?.should_subscribe);
    })();

    return () => {
      cancelled = true;
    };
  }, [calculationId, fetchAriesLatest]);

  // Auto-download on completion: the moment a LIVE run finishes with a bundle,
  // fire the same same-tab download the completion view's button triggers —
  // whether the modal is open or the run was backgrounded ("Run in background"
  // only hides the modal; this hook keeps tracking the run).
  //
  // The armed ref scopes the auto-fire to runs watched in-flight this session:
  // it arms only while a live run is building (fresh kickoff, or a restored
  // still-in-progress run), fires exactly once on completion, and disarms
  // whenever no run is live — so a restored already-terminal run (route change /
  // page reload re-offering an old bundle) never re-downloads on its own.
  const ariesMdbAutoDownloadArmed = useRef(false);
  const ariesMdbBundleUrl = ariesMdbProgress.bundle?.downloadUrl;
  useEffect(() => {
    if (!ariesMdbRunStarted) {
      ariesMdbAutoDownloadArmed.current = false;
      return;
    }
    if (ariesMdbProgress.isBuilding) {
      ariesMdbAutoDownloadArmed.current = true;
      return;
    }
    // Overall `processed` and the bundle step's `output` arrive as separate
    // Pusher events in no guaranteed order; this effect re-runs on both, so it
    // fires once both are in hand.
    if (
      ariesMdbAutoDownloadArmed.current &&
      ariesMdbProgress.isComplete &&
      ariesMdbBundleUrl
    ) {
      ariesMdbAutoDownloadArmed.current = false;
      // Dismiss the modal (still open on the live progress view unless the run
      // was backgrounded) BEFORE firing the download — the export is done and
      // the file is saving, so there's no completion dialog to close by hand.
      setIsAriesMdbModalOpen(false);
      downloadFromUrl(ariesMdbBundleUrl);
    }
  }, [
    ariesMdbRunStarted,
    ariesMdbProgress.isBuilding,
    ariesMdbProgress.isComplete,
    ariesMdbBundleUrl,
  ]);

  return {
    open: handleOpenAriesMdbModal,
    modalProps: {
      isOpen: isAriesMdbModalOpen,
      isLoading: ariesMdbExport.isSubmitting,
      isLoadingDefaults: ariesMdbExport.isLoadingDefaults,
      defaults: ariesMdbExport.defaults,
      errorMessage: ariesMdbExport.error,
      runStarted: ariesMdbRunStarted,
      restoredOutputName: ariesMdbRestore?.config?.outputName ?? null,
      statusItems: ariesMdbProgress.statusItems,
      overallStatus: ariesMdbProgress.overallStatus,
      bundle: ariesMdbProgress.bundle,
      onClose: handleCloseAriesMdbModal,
      onSubmit: handleSubmitAriesMdb,
      onRunInBackground: handleRunInBackgroundAriesMdb,
    },
  };
}
